import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { mediaSrc, uploadMedia } from '../lib/api';
import { CAT_LABEL, CAT_COLOR } from '../lib/blockColors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Inbox, Plus, Sparkles, CheckCircle2, Circle, Trash2, MoreVertical, Zap, Bell, ListChecks, Loader2, Send, CornerDownRight, Repeat, CalendarClock, ArrowUpRight, Paperclip, FileText, Download, Upload, X, Mic, Square, Flag, Sprout, Wind } from 'lucide-react';
import { SubtaskList } from '../components/SubtaskList';

const ENERGY = { low: 'Low energy', medium: 'Medium', high: 'High energy' };
const STATUS_BADGE = { inbox: 'Inbox', todo: 'To do', scheduled: 'Scheduled', done: 'Done', archived: 'Archived' };
// Jev-scored priority tiers (fast, free triage)
const TIER = {
  do_first: { label: 'Do first', color: 'hsl(var(--brand-coral))' },
  soon: { label: 'Soon', color: 'hsl(var(--brand-honey))' },
  whenever: { label: 'Whenever', color: 'hsl(var(--muted-foreground))' },
};
const TIER_RANK = { do_first: 0, soon: 1, whenever: 2 };
const WEEKDAYS = [['M', 0], ['T', 1], ['W', 2], ['T', 3], ['F', 4], ['S', 5], ['S', 6]];

const recurrenceLabel = (rec) => {
  if (!rec || !rec.freq) return null;
  const n = rec.interval && rec.interval > 1 ? `${rec.interval} ` : '';
  if (rec.freq === 'daily') return `Every ${n}day${rec.interval > 1 ? 's' : ''}`;
  if (rec.freq === 'monthly') return `Every ${n}month${rec.interval > 1 ? 's' : ''}`;
  if (rec.freq === 'weekly') {
    if (rec.weekdays?.length) {
      const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return `Weekly · ${rec.weekdays.slice().sort().map((d) => names[d]).join(', ')}`;
    }
    return `Every ${n}week${rec.interval > 1 ? 's' : ''}`;
  }
  return 'Repeats';
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('open');
  const [capture, setCapture] = useState('');
  const [detail, setDetail] = useState(null);
  const [breakingDown, setBreakingDown] = useState(false);
  const [triaging, setTriaging] = useState(false);
  const [easeBusy, setEaseBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attBusy, setAttBusy] = useState(false);
  const attRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recTarget, setRecTarget] = useState(null); // 'capture' | 'notes' | 'subtask'
  const [sttBusy, setSttBusy] = useState(false);
  const [subInput, setSubInput] = useState('');
  const [addingSub, setAddingSub] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const applyRef = useRef(null);

  const transcribe = async (blob) => {
    setSttBusy(true);
    const fd = new FormData();
    fd.append('file', blob, 'braindump.webm');
    try {
      const res = await api.post('/voice/stt', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const text = (res.data?.text || '').trim();
      if (text && applyRef.current) applyRef.current(text);
      else if (!text) toast.error("Didn't catch that — try again");
    } catch (e) { toast.error('Could not transcribe audio'); }
    finally { setSttBusy(false); applyRef.current = null; }
  };

  const toggleRecord = async (target = 'capture', applyFn = null) => {
    if (recording) {
      try { mediaRecRef.current && mediaRecRef.current.stop(); } catch (e) {}
      setRecording(false); setRecTarget(null);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Microphone not available in this browser'); return; }
    applyRef.current = applyFn || ((text) => setCapture((c) => (c ? c + ' ' : '') + text));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) transcribe(blob);
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true); setRecTarget(target);
    } catch (e) { toast.error('Microphone permission denied'); }
  };

  const appendNotesDictation = (text) => {
    setDetail((d) => {
      if (!d) return d;
      const notes = (d.notes ? d.notes + ' ' : '') + text;
      const updated = { ...d, notes };
      setTasks((l) => l.map((x) => x.task_id === d.task_id ? updated : x));
      api.put(`/tasks/${d.task_id}`, { title: d.title, notes, estimate_min: d.estimate_min, energy: d.energy, nag: d.nag, due_date: d.due_date ?? '', recurrence: d.recurrence ?? {} }).catch(() => {});
      return updated;
    });
  };

  const addSubtask = async (title) => {
    const t = (title || '').trim();
    if (!t || !detail) return;
    setAddingSub(true);
    try {
      await api.post('/tasks', { title: t, parent_id: detail.task_id, status: 'todo' });
      setSubInput('');
      await load();
    } catch (e) { toast.error('Could not add subtask'); }
    finally { setAddingSub(false); }
  };

  const load = useCallback(async () => { const res = await api.get('/tasks'); setTasks(res.data); }, []);
  useEffect(() => { load(); }, [load]);
  const [catColors, setCatColors] = useState({});
  useEffect(() => { api.get('/settings').then((r) => setCatColors(r.data?.block_colors?.cat || {})).catch(() => {}); }, []);
  const catColor = (k) => catColors[k] || CAT_COLOR[k] || CAT_COLOR.admin;

  const loadAttachments = useCallback(async (taskId) => {
    if (!taskId) { setAttachments([]); return; }
    try { const res = await api.get(`/media?context=task&ref_id=${taskId}`); setAttachments(res.data); }
    catch (e) { setAttachments([]); }
  }, []);
  useEffect(() => { loadAttachments(detail?.task_id); }, [detail?.task_id, loadAttachments]);

  const uploadTaskFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (attRef.current) attRef.current.value = '';
    if (!files.length || !detail) return;
    setAttBusy(true);
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is over 25 MB`); continue; }
      try { const media = await uploadMedia(file, 'task', detail.task_id); setAttachments((a) => [media, ...a]); }
      catch (err) { toast.error(`Couldn't upload ${file.name}`); }
    }
    setAttBusy(false);
    toast.success('Attached to task');
  };

  const deleteTaskFile = async (id) => {
    setAttachments((a) => a.filter((x) => x.media_id !== id));
    try { await api.delete(`/media/${id}`); } catch (e) { toast.error('Could not remove'); loadAttachments(detail?.task_id); }
  };

  const childrenOf = (pid) => tasks.filter((t) => t.parent_id === pid).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const filtered = tasks.filter((t) => {
    if (t.parent_id) return false; // subtasks render nested under parents
    if (tab === 'open') return ['inbox', 'todo', 'scheduled'].includes(t.status);
    if (tab === 'done') return t.status === 'done';
    return t.status === 'archived';
  });
  if (tab === 'open') {
    filtered.sort((a, b) => (TIER_RANK[a.priority_tier] ?? 3) - (TIER_RANK[b.priority_tier] ?? 3) || (a.order ?? 0) - (b.order ?? 0));
  }

  const breakdownTask = async (task) => {
    if (!task) return;
    const tid = task.task_id;
    setDetail({ ...task });
    setBreakingDown(true);
    try {
      const res = await api.post(`/tasks/${tid}/breakdown`);
      const updated = { ...task, steps: res.data.steps, status: task.status === 'inbox' ? 'todo' : task.status, needs_breakdown: false };
      setDetail(updated);
      setTasks((l) => l.map((x) => x.task_id === tid ? updated : x));
      toast.success('Broken into tiny steps \u2014 add the ones you want as subtasks.');
    } catch (e) { toast.error('Could not break it down'); } finally { setBreakingDown(false); }
  };

  const applyLocal = (partial) => {
    if (!detail) return;
    const u = { ...detail, ...partial };
    setDetail(u);
    setTasks((l) => l.map((x) => x.task_id === detail.task_id ? u : x));
  };

  const sortMyDay = async () => {
    setTriaging(true);
    try {
      const res = await api.post('/tasks/triage');
      await load();
      const top = res.data?.top3 || [];
      if (!top.length) { toast('Nothing to triage yet \u2014 capture a few tasks first.'); }
      else {
        toast.success(`Your Do-first for today (${res.data.do_first_count})`, {
          description: top.map((t, i) => `${i + 1}. ${t.title}${t.is_sticky ? '  \u2022 ugh task' : ''}`).join('\n'),
          duration: 10000,
        });
      }
    } catch (e) { toast.error('Could not sort your day'); }
    finally { setTriaging(false); }
  };

  const easeIn = async () => {
    if (!detail) return;
    setEaseBusy(true);
    try {
      const res = await api.post(`/tasks/${detail.task_id}/ease-in`);
      applyLocal({ ease_in_step: res.data.step });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not get a starter step'); }
    finally { setEaseBusy(false); }
  };

  const quickAdd = async () => {
    if (!capture.trim()) return;
    const text = capture.trim();
    setCapture('');
    try {
      const res = await api.post('/tasks/capture', { text, status: 'inbox' });
      const created = res.data?.tasks || [];
      setTasks((t) => [...created, ...t]);
      if (res.data?.split && created.length > 1) {
        toast.success(`Split into ${created.length} tasks`, { description: created.map((c) => c.title).join(' \u00b7 ') });
      }
      const big = created.find((c) => c?.needs_breakdown);
      if (big && !res.data?.split) {
        toast('This looks like a big one. Want to split it into tiny steps?', {
          icon: <ListChecks className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />,
          duration: 12000,
          action: { label: 'Split it', onClick: () => breakdownTask(big) },
          className: 'larkwell-nudge',
        });
      }
      const dup = res.data?.possible_duplicate;
      if (dup && created[0]) {
        toast('Possible duplicate', {
          description: `Looks a lot like \u201c${dup.title}\u201d.`,
          duration: 10000,
          action: {
            label: 'Remove new',
            onClick: async () => {
              try { await api.delete(`/tasks/${created[0].task_id}`); setTasks((l) => l.filter((x) => x.task_id !== created[0].task_id)); toast.success('Removed the duplicate'); }
              catch (e) { toast.error('Could not remove'); }
            },
          },
        });
      }
    }
    catch (e) { toast.error('Could not capture'); setCapture(text); }
  };

  const complete = async (t) => {
    const res = await api.post(`/tasks/${t.task_id}/complete`);
    setTasks((list) => {
      let next = list.map((x) => x.task_id === t.task_id ? { ...x, status: 'done' } : x);
      if (res.data?.spawned) next = [res.data.spawned, ...next];
      return next;
    });
    const lines = ['Small win logged.', 'Nice \u2014 one less thing to hold in your head.', 'Momentum unlocked. \u2728'];
    toast.success(lines[Math.floor(Math.random() * lines.length)]);
    if (res.data?.spawned) toast('Next one is scheduled \u2014 future you is covered.', { icon: '\uD83D\uDD01' });
  };

  const setStatus = async (t, status) => { await api.put(`/tasks/${t.task_id}`, { title: t.title, status }); load(); };
  const del = async (t) => {
    await api.delete(`/tasks/${t.task_id}`);
    setTasks((l) => l.filter((x) => x.task_id !== t.task_id && x.parent_id !== t.task_id));
    if (detail?.task_id === t.task_id) setDetail(null);
  };

  const promote = async (t) => {
    await api.post(`/tasks/${t.task_id}/promote`, {});
    setTasks((l) => l.map((x) => x.task_id === t.task_id ? { ...x, parent_id: null } : x));
    toast.success('Moved out as its own task.');
  };

  const reorderSubs = async (ids) => {
    setTasks((prev) => prev.map((t) => { const i = ids.indexOf(t.task_id); return i >= 0 ? { ...t, order: i } : t; }));
    try { await api.post('/tasks/reorder', { task_ids: ids }); } catch (e) { toast.error('Could not reorder'); }
  };

  const openDetail = (t) => setDetail(t);

  const runBreakdown = async () => {
    if (!detail) return;
    setBreakingDown(true);
    try {
      const res = await api.post(`/tasks/${detail.task_id}/breakdown`);
      const updated = { ...detail, steps: res.data.steps, status: detail.status === 'inbox' ? 'todo' : detail.status };
      setDetail(updated);
      setTasks((l) => l.map((x) => x.task_id === detail.task_id ? updated : x));
      toast.success('Broken into tiny steps \u2014 add the ones you want as subtasks.');
    } catch (e) { toast.error('Could not break it down'); } finally { setBreakingDown(false); }
  };

  const toggleStep = async (idx) => {
    const steps = detail.steps.map((s, i) => i === idx ? { ...s, done: !s.done } : s);
    const updated = { ...detail, steps };
    setDetail(updated);
    setTasks((l) => l.map((x) => x.task_id === detail.task_id ? updated : x));
    await api.put(`/tasks/${detail.task_id}`, { title: detail.title, steps });
  };

  const addStepsAsSubtasks = async (indices) => {
    if (!detail) return;
    try {
      const res = await api.post(`/tasks/${detail.task_id}/steps-to-subtasks`, { indices: indices ?? null });
      const { created, remaining_steps } = res.data;
      const updated = { ...detail, steps: remaining_steps };
      setDetail(updated);
      setTasks((l) => l.map((x) => x.task_id === detail.task_id ? updated : x).concat(created));
      toast.success(`Added ${created.length} subtask${created.length === 1 ? '' : 's'} to your list.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add subtasks'); }
  };

  const saveDetail = async (partial) => {
    const updated = { ...detail, ...partial };
    setDetail(updated);
    setTasks((l) => l.map((x) => x.task_id === detail.task_id ? updated : x));
    await api.put(`/tasks/${detail.task_id}`, {
      title: updated.title, notes: updated.notes, estimate_min: updated.estimate_min,
      energy: updated.energy, category: updated.category, nag: updated.nag, due_date: updated.due_date ?? '',
      recurrence: updated.recurrence ?? {},
    });
  };

  const rec = detail?.recurrence || {};
  const setRecurrence = (next) => saveDetail({ recurrence: next });
  const toggleWeekday = (d) => {
    const cur = new Set(rec.weekdays || []);
    if (cur.has(d)) cur.delete(d); else cur.add(d);
    setRecurrence({ ...rec, freq: 'weekly', weekdays: Array.from(cur) });
  };

  const detailSubs = detail ? childrenOf(detail.task_id) : [];

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-[1fr_420px] min-h-0">
      {/* Left: capture + inbox */}
      <div className="flex flex-col min-h-0 border-r border-[hsl(var(--border))]">
        <div className="px-4 sm:px-6 py-4 border-b border-[hsl(var(--border))]">
          <h1 className="font-display text-xl font-700 flex items-center gap-2"><Inbox className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Brain dump</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Drop anything here. We'll sort it later.</p>
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2 shadow-sm">
            <Zap className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <Input value={capture} onChange={(e) => setCapture(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') quickAdd(); }} placeholder={recTarget === 'capture' ? 'Listening… tap the square to stop' : 'Type a thought… (press Enter)'} className="border-0 shadow-none focus-visible:ring-0 h-8 px-0" data-testid="quick-capture-input" />
            <Button size="icon" variant="ghost" className={`h-8 w-8 rounded-lg shrink-0 ${recTarget === 'capture' ? 'text-[hsl(var(--destructive))] animate-pulse' : 'text-[hsl(var(--muted-foreground))]'}`} onClick={() => toggleRecord('capture')} disabled={sttBusy && recTarget !== 'capture'} title="Voice brain-dump" data-testid="quick-capture-mic-button">
              {sttBusy && recTarget === 'capture' ? <Loader2 className="h-4 w-4 animate-spin" /> : recTarget === 'capture' ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button size="icon" className="h-8 w-8 rounded-lg shrink-0" style={{ background: 'hsl(var(--primary))' }} onClick={quickAdd} data-testid="quick-capture-submit-button"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 border-b border-[hsl(var(--border))] flex items-center gap-2">
          <Tabs value={tab} onValueChange={setTab}><TabsList><TabsTrigger value="open" data-testid="tasks-tab-open">Open</TabsTrigger><TabsTrigger value="done" data-testid="tasks-tab-done">Done</TabsTrigger><TabsTrigger value="archived">Archived</TabsTrigger></TabsList></Tabs>
          {tab === 'open' && (
            <Button size="sm" variant="secondary" className="ml-auto h-8 rounded-lg text-xs gap-1 border border-[hsl(var(--border))]" onClick={sortMyDay} disabled={triaging} title="Jev scores what to do first" data-testid="sort-my-day-button">
              {triaging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Flag className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-coral))' }} />} Sort my day
            </Button>
          )}
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4 sm:p-6 space-y-2">
            {filtered.length === 0 && <div className="text-center py-14"><div className="h-12 w-12 rounded-2xl mx-auto grid place-items-center mb-3" style={{ background: 'hsl(var(--accent) / 0.12)' }}><ListChecks className="h-6 w-6" style={{ color: 'hsl(var(--accent))' }} /></div><p className="font-600">Nothing here</p><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Capture a thought above to get started.</p></div>}
            <AnimatePresence>
              {filtered.map((t) => {
                const subs = childrenOf(t.task_id);
                const rl = recurrenceLabel(t.recurrence);
                return (
                <motion.div key={t.task_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -8 }}>
                  <div
                    className={`flex items-start gap-3 rounded-2xl border border-[hsl(var(--border))] bg-card p-3.5 hover:shadow-md cursor-pointer ${detail?.task_id === t.task_id ? 'ring-2 ring-[hsl(var(--primary)/0.3)]' : ''}`} onClick={() => openDetail(t)} data-testid={`task-card-${t.task_id}`}>
                    <button onClick={(e) => { e.stopPropagation(); complete(t); }} className="mt-0.5 shrink-0" data-testid={`task-complete-${t.task_id}`}>
                      <CheckCircle2 className={`h-5 w-5 ${t.status === 'done' ? 'text-[hsl(var(--success))] fill-[hsl(var(--success)/0.15)]' : 'text-[hsl(var(--muted-foreground))]'}`} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {t.category && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: catColor(t.category) }} title={CAT_LABEL[t.category] || t.category} data-testid={`task-category-dot-${t.task_id}`} />}
                        <p className={`text-sm font-500 ${t.status === 'done' ? 'line-through text-[hsl(var(--muted-foreground))]' : ''}`}>{t.title}</p>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-[9px]">{STATUS_BADGE[t.status]}</Badge>
                        {t.priority_tier && TIER[t.priority_tier] && <Badge variant="outline" className="text-[9px] gap-1" style={{ color: TIER[t.priority_tier].color, borderColor: TIER[t.priority_tier].color }} data-testid={`task-tier-badge-${t.task_id}`}><Flag className="h-2.5 w-2.5" />{TIER[t.priority_tier].label}</Badge>}
                        {t.is_sticky && t.status !== 'done' && <Badge variant="outline" className="text-[9px] gap-1" style={{ color: 'hsl(var(--brand-coral))', borderColor: 'hsl(var(--brand-coral) / 0.5)' }} title="Easy to put off \u2014 open it to ease in" data-testid={`task-sticky-badge-${t.task_id}`}><Wind className="h-2.5 w-2.5" />Ugh task</Badge>}
                        {t.category && <Badge variant="outline" className="text-[9px]" style={{ color: catColor(t.category), borderColor: catColor(t.category) }} data-testid={`task-category-badge-${t.task_id}`}>{CAT_LABEL[t.category] || t.category}</Badge>}
                        {t.energy && <Badge variant="outline" className="text-[9px] gap-1" data-testid={`task-energy-badge-${t.task_id}`}><Zap className="h-2.5 w-2.5" style={{ color: t.energy === 'high' ? 'hsl(var(--destructive))' : t.energy === 'low' ? 'hsl(var(--success))' : 'hsl(var(--brand-honey))' }} />{t.energy === 'high' ? 'High' : t.energy === 'low' ? 'Low' : 'Med'} energy</Badge>}
                        {subs.length > 0 && <Badge variant="outline" className="text-[9px] gap-1" data-testid={`subtask-count-${t.task_id}`}><CornerDownRight className="h-2.5 w-2.5" />{subs.filter((s) => s.status === 'done').length}/{subs.length} subtasks</Badge>}
                        {t.steps?.length > 0 && <Badge variant="outline" className="text-[9px] gap-1"><ListChecks className="h-2.5 w-2.5" />{t.steps.filter((s) => s.done).length}/{t.steps.length} steps</Badge>}
                        {rl && <Badge className="text-[9px] gap-1" style={{ background: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' }}><Repeat className="h-2.5 w-2.5" />{rl}</Badge>}
                        {t.nag && <Badge className="text-[9px] gap-1" style={{ background: 'hsl(var(--warning) / 0.14)', color: 'hsl(var(--foreground))' }}><Bell className="h-2.5 w-2.5" />Nudging</Badge>}
                        {t.needs_breakdown && !subs.length && !(t.steps?.length) && (
                          <button onClick={(e) => { e.stopPropagation(); breakdownTask(t); }} data-testid={`task-breakdown-nudge-${t.task_id}`}>
                            <Badge className="text-[9px] gap-1 cursor-pointer transition-transform hover:scale-105" style={{ background: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' }}><ListChecks className="h-2.5 w-2.5" />Big — split?</Badge>
                          </button>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button onClick={(e) => e.stopPropagation()} className="text-[hsl(var(--muted-foreground))]" data-testid={`task-menu-${t.task_id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => setStatus(t, 'todo')}>Move to To do</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(t, 'archived')}>Archive</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => del(t)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Nested subtasks (drag to reorder, drag out to standalone) */}
                  {subs.length > 0 && (
                    <div className="mt-1.5 ml-5 pl-3 border-l-2 border-[hsl(var(--border))]">
                      <SubtaskList
                        subs={subs}
                        onReorder={reorderSubs}
                        onComplete={complete}
                        onPromote={promote}
                        onDelete={del}
                      />
                    </div>
                  )}
                </motion.div>
              ); })}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </div>

      {/* Right: detail + breakdown */}
      <div className="hidden lg:flex flex-col min-h-0 bg-[hsl(var(--surface-2))]">
        {!detail ? (
          <div className="flex-1 grid place-items-center p-8 text-center">
            <div>
              <div className="h-14 w-14 rounded-2xl mx-auto grid place-items-center mb-3" style={{ background: 'hsl(var(--primary) / 0.1)' }}><Sparkles className="h-7 w-7" style={{ color: 'hsl(var(--primary))' }} /></div>
              <p className="font-600">Pick a task</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xs">Select anything on the left, then let Larkwell break it into tiny doable steps.</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="p-6 space-y-4">
              <Input value={detail.title} onChange={(e) => setDetail({ ...detail, title: e.target.value })} onBlur={() => saveDetail({})} className="text-base font-600 rounded-xl bg-card" data-testid="task-detail-title" />
              <div className="relative">
                <Textarea value={detail.notes || ''} onChange={(e) => setDetail({ ...detail, notes: e.target.value })} onBlur={() => saveDetail({})} placeholder="Notes, context, anything…" className="min-h-[70px] rounded-xl bg-card pr-11" data-testid="task-detail-notes" />
                <Button size="icon" variant="ghost" className={`absolute top-1.5 right-1.5 h-8 w-8 rounded-lg ${recTarget === 'notes' ? 'text-[hsl(var(--destructive))] animate-pulse' : 'text-[hsl(var(--muted-foreground))]'}`} onClick={() => toggleRecord('notes', appendNotesDictation)} disabled={sttBusy && recTarget !== 'notes'} title="Dictate notes" data-testid="task-notes-mic">
                  {sttBusy && recTarget === 'notes' ? <Loader2 className="h-4 w-4 animate-spin" /> : recTarget === 'notes' ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Estimate</label>
                  <Select value={String(detail.estimate_min || 30)} onValueChange={(v) => saveDetail({ estimate_min: Number(v) })}><SelectTrigger className="h-9 rounded-xl mt-1 bg-card"><SelectValue /></SelectTrigger><SelectContent>{[15, 30, 45, 60, 90, 120].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent></Select>
                </div>
                <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Energy</label>
                  <Select value={detail.energy || 'medium'} onValueChange={(v) => saveDetail({ energy: v })}><SelectTrigger className="h-9 rounded-xl mt-1 bg-card" data-testid="task-energy-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ENERGY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div><label className="text-xs text-[hsl(var(--muted-foreground))]">Type</label>
                <Select value={detail.category || 'admin'} onValueChange={(v) => saveDetail({ category: v })}><SelectTrigger className="h-9 rounded-xl mt-1 bg-card" data-testid="task-category-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </div>

              {/* Due date */}
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Due date</label>
                <Input type="date" value={detail.due_date || ''} onChange={(e) => saveDetail({ due_date: e.target.value })} className="h-9 rounded-xl mt-1 bg-card" data-testid="task-due-input" />
              </div>

              {/* Recurrence */}
              <div className="rounded-xl border border-[hsl(var(--border))] bg-card p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  <p className="text-sm font-500 flex-1">Repeat</p>
                  <Select value={rec.freq || 'none'} onValueChange={(v) => setRecurrence(v === 'none' ? {} : { freq: v, interval: rec.interval || 1, weekdays: v === 'weekly' ? (rec.weekdays || []) : undefined })}>
                    <SelectTrigger className="h-8 w-32 rounded-lg" data-testid="task-repeat-freq"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Doesn't repeat</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {rec.freq && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">Every</span>
                    <Input type="number" min={1} max={52} value={rec.interval || 1} onChange={(e) => setRecurrence({ ...rec, interval: Math.max(1, Number(e.target.value) || 1) })} className="h-8 w-16 rounded-lg" data-testid="task-repeat-interval" />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{rec.freq === 'daily' ? 'day(s)' : rec.freq === 'weekly' ? 'week(s)' : 'month(s)'}</span>
                  </div>
                )}
                {rec.freq === 'weekly' && (
                  <div className="flex items-center gap-1.5" data-testid="task-repeat-weekdays">
                    {WEEKDAYS.map(([label, d]) => {
                      const active = (rec.weekdays || []).includes(d);
                      return (
                        <button key={d} onClick={() => toggleWeekday(d)} type="button"
                          className={`h-8 w-8 rounded-full text-xs font-600 transition-colors ${active ? 'text-white' : 'text-[hsl(var(--muted-foreground))] bg-[hsl(var(--secondary))]'}`}
                          style={active ? { background: 'hsl(var(--primary))' } : {}}
                          data-testid={`weekday-${d}`}>{label}</button>
                      );
                    })}
                  </div>
                )}
                {recurrenceLabel(detail.recurrence) && (
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">When you finish this, Larkwell creates the next one automatically.</p>
                )}
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2.5">
                <Bell className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                <div className="flex-1"><p className="text-sm font-500">Nag mode</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">Gentle repeated nudges until done</p></div>
                <Switch checked={!!detail.nag} onCheckedChange={(v) => saveDetail({ nag: v })} data-testid="task-nag-toggle" />
              </div>

              {/* Attachments */}
              <div className="rounded-xl border border-[hsl(var(--border))] bg-card p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  <p className="text-sm font-500 flex-1">Attachments</p>
                  <input ref={attRef} type="file" multiple className="hidden" onChange={uploadTaskFile} data-testid="task-attach-input" />
                  <Button size="sm" variant="secondary" className="h-8 rounded-lg text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => attRef.current?.click()} disabled={attBusy} data-testid="task-attach-button">
                    {attBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Add file
                  </Button>
                </div>
                {attachments.length === 0 ? (
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Attach reference docs, screenshots or anything you need for this task (max 25 MB each).</p>
                ) : (
                  <div className="space-y-1.5" data-testid="task-attachments-list">
                    {attachments.map((a) => (
                      <div key={a.media_id} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-1))] px-2 py-1.5">
                        {a.is_image ? (
                          <img src={mediaSrc(a.media_id)} alt={a.original_filename} className="h-8 w-8 rounded object-cover shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
                        )}
                        <a href={mediaSrc(a.media_id)} target="_blank" rel="noreferrer" download={a.original_filename} className="text-sm truncate flex-1 hover:underline" data-testid={`task-attachment-${a.media_id}`}>{a.original_filename}</a>
                        <a href={mediaSrc(a.media_id)} target="_blank" rel="noreferrer" download={a.original_filename} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] shrink-0"><Download className="h-3.5 w-3.5" /></a>
                        <button onClick={() => deleteTaskFile(a.media_id)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] shrink-0" data-testid={`task-attachment-delete-${a.media_id}`}><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.is_sticky && (
                <div className="rounded-2xl border p-4" style={{ borderColor: 'hsl(var(--brand-coral) / 0.4)', background: 'hsl(var(--brand-coral) / 0.06)' }} data-testid="ease-in-card">
                  <p className="text-sm font-600 flex items-center gap-1.5"><Wind className="h-4 w-4" style={{ color: 'hsl(var(--brand-coral))' }} /> Feeling the resistance?</p>
                  <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">Jev flagged this as an easy-to-avoid task. Shrink it to a 2-minute start.</p>
                  {detail.ease_in_step ? (
                    <div className="mt-2 rounded-xl bg-card border border-[hsl(var(--border))] p-3 flex items-start gap-2" data-testid="ease-in-step">
                      <Sprout className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--brand-mint))' }} />
                      <p className="text-sm">{detail.ease_in_step}</p>
                    </div>
                  ) : (
                    <Button size="sm" className="mt-2 rounded-xl gap-1.5 text-white" style={{ background: 'hsl(var(--brand-coral))' }} onClick={easeIn} disabled={easeBusy} data-testid="ease-in-button">
                      {easeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sprout className="h-4 w-4" />} Give me a 2-minute start
                    </Button>
                  )}
                </div>
              )}

              <Button className="w-full rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={runBreakdown} disabled={breakingDown} data-testid="task-breakdown-button">
                {breakingDown ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Break down with AI
              </Button>

              {detail.steps?.length > 0 && (
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-600 text-[hsl(var(--muted-foreground))]">TINY STEPS</p>
                    <Button size="sm" variant="secondary" className="h-7 rounded-lg text-xs gap-1 border border-[hsl(var(--border))]" onClick={() => addStepsAsSubtasks(null)} data-testid="add-all-subtasks-button">
                      <Plus className="h-3.5 w-3.5" /> Add all as subtasks
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {detail.steps.map((s, i) => (
                      <div key={i} className="w-full flex items-start gap-2 rounded-xl p-1.5 hover:bg-[hsl(var(--secondary))]">
                        <button onClick={() => toggleStep(i)} className="flex items-start gap-2 text-left flex-1" data-testid={`task-step-${i}`}>
                          <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${s.done ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--muted-foreground))]'}`} />
                          <span className={`text-sm ${s.done ? 'line-through text-[hsl(var(--muted-foreground))]' : ''}`}>{i === 0 && !s.done ? <span className="font-600" style={{ color: 'hsl(var(--primary))' }}>{'Start here → '}</span> : ''}{s.text}</span>
                        </button>
                        <button onClick={() => addStepsAsSubtasks([i])} title="Add as subtask" className="mt-0.5 shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`add-step-subtask-${i}`}>
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subtasks (add + list) in detail */}
              <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4">
                <p className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><CornerDownRight className="h-3.5 w-3.5" /> SUBTASKS</p>
                <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-1))] px-2.5 py-1.5 mb-2">
                  <Input value={subInput} onChange={(e) => setSubInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addSubtask(subInput); }} placeholder={recTarget === 'subtask' ? 'Listening… tap square to stop' : 'Add a step… (type or talk)'} className="border-0 shadow-none focus-visible:ring-0 h-7 px-0 text-sm" data-testid="subtask-add-input" />
                  <Button size="icon" variant="ghost" className={`h-7 w-7 rounded-lg shrink-0 ${recTarget === 'subtask' ? 'text-[hsl(var(--destructive))] animate-pulse' : 'text-[hsl(var(--muted-foreground))]'}`} onClick={() => toggleRecord('subtask', (text) => setSubInput((c) => (c ? c + ' ' : '') + text))} disabled={sttBusy && recTarget !== 'subtask'} title="Dictate a subtask" data-testid="subtask-mic">
                    {sttBusy && recTarget === 'subtask' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : recTarget === 'subtask' ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                  </Button>
                  <Button size="icon" className="h-7 w-7 rounded-lg shrink-0" style={{ background: 'hsl(var(--primary))' }} onClick={() => addSubtask(subInput)} disabled={addingSub || !subInput.trim()} data-testid="subtask-add-button">{addingSub ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}</Button>
                </div>
                {detailSubs.length > 0 && (
                  <div className="space-y-1">
                    {detailSubs.map((sub) => (
                      <div key={sub.task_id} className="flex items-center gap-2 rounded-lg py-1.5 px-1.5 hover:bg-[hsl(var(--secondary))]">
                        <button onClick={() => complete(sub)}>
                          {sub.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> : <Circle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
                        </button>
                        <span className={`text-sm flex-1 ${sub.status === 'done' ? 'line-through text-[hsl(var(--muted-foreground))]' : ''}`}>{sub.title}</span>
                        <button onClick={() => promote(sub)} title="Make standalone" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"><ArrowUpRight className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(sub)} title="Delete" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
